/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { Container } from '@adobe-commerce/elsie/lib';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { AddressValidation as AddressValidationComponent } from '@/account/components';
import { CustomerAddressesModel } from '@/account/data/models';

export interface AddressValidationProps {
  selectedAddress?: 'suggested' | 'original' | null;
  suggestedAddress: Partial<CustomerAddressesModel> | null;
  originalAddress: CustomerAddressesModel | null;
  handleSelectedAddress?: (payload: {
    selection: 'suggested' | 'original';
    address: CustomerAddressesModel | null | undefined;
  }) => void;
}

export const AddressValidation: Container<AddressValidationProps> = ({
  selectedAddress = null,
  suggestedAddress,
  originalAddress,
  handleSelectedAddress,
}) => {
  const [selection, setSelection] = useState<'suggested' | 'original' | null>(
    selectedAddress
  );
  const [busy, setBusy] = useState(false);

  const mergedSuggestedAddress = useMemo<CustomerAddressesModel | null>(() => {
    if (!suggestedAddress || !originalAddress) return null;
    return { ...originalAddress, ...suggestedAddress };
  }, [originalAddress, suggestedAddress]);

  const onSelectionChange = useCallback(
    async (sel: 'suggested' | 'original') => {
      setSelection(sel);
      const chosen =
        sel === 'suggested' ? mergedSuggestedAddress : originalAddress;
      setBusy(true);
      try {
        await handleSelectedAddress?.({ selection: sel, address: chosen });
      } finally {
        setBusy(false);
      }
    },
    [mergedSuggestedAddress, originalAddress, handleSelectedAddress]
  );

  useEffect(() => {
    if (!selection) return;
    onSelectionChange(selection);
  }, [selection, onSelectionChange]);

  return (
    <AddressValidationComponent
      busy={busy}
      originalAddress={originalAddress}
      selection={selection}
      suggestedAddress={mergedSuggestedAddress}
      onSelectionChange={onSelectionChange}
    />
  );
};
